import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMealPlan } from '../../contexts/MealPlanContext';
import { capDisplayFreshness } from '../../utils/freshnessDisplay';
import { useCookingStates } from '../../hooks/useCookingStates';
import RecipeContent from '../Recipes/RecipeContent';

const RecipeDetailPage = () => {
  const { recipeIndex } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('mealplan');
  const { t: tRecipes } = useTranslation('recipes');
  const { mealPlan, productList } = useMealPlan();
  const { cookingStates, startCooking } = useCookingStates();

  const index = parseInt(recipeIndex, 10);
  const recipe = Array.isArray(mealPlan) ? mealPlan[index] : null;

  if (!recipe) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-16 text-center">
        <p className="text-[#65635D] text-[15px]">{t('loading.title')}</p>
      </div>
    );
  }

  const recipeId = recipe.id || recipe.title;
  const cookingState = cookingStates[recipeId];
  const isCookingStarted = cookingState?.startedAt && !cookingState?.isCompleted;

  const mealTypeKey = (recipe.mealType || '').toLowerCase();
  const localizedMealType = mealTypeKey
    ? t(`mealTypes.${mealTypeKey}`, { defaultValue: recipe.mealType })
    : null;
  const cookingTimeMinutes = (recipe.cookingTime || recipe.cooking_time)
    ? String(recipe.cookingTime || recipe.cooking_time).replace(/\s*min\s*$/i, '').trim()
    : null;
  const freshDays = (recipe.freshness || recipe.maxStorageTime)
    ? capDisplayFreshness(parseInt(recipe.freshness || recipe.maxStorageTime, 10) || 0)
    : null;

  const description =
    (recipe.instructions && recipe.instructions.full_description) || recipe.description;

  const handleStartCooking = async () => {
    if (!cookingState?.isCompleted) {
      await startCooking(recipeId);
    }
    navigate(`/mymeals/recipe/${index}/cook`);
  };

  return (
    <div className="min-h-screen bg-white pb-32">
      <div className="w-full max-w-5xl mx-auto lg:py-5 lg:px-1">
      {/* Hero image — full width, edge to edge */}
      <div className="w-full lg:rounded-[12px] overflow-hidden lg:px-5">
        {recipe.imageURL ? (
          <img
            src={recipe.imageURL}
            alt={recipe.title}
            className="w-full object-cover max-h-[55vh] lg:max-h-[40vh] lg:rounded-[12px]"
            onError={(e) => {
              e.target.src = '/assets/products/no-image.jpg';
            }}
          />
        ) : (
          <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
            <span className="text-[#65635D] text-[14px]">{t('loading.imageNotAvailable')}</span>
          </div>
        )}
      </div>

      <div className="px-5 pt-5">
        {/* Meta */}
        <div className="flex flex-row flex-wrap gap-1 mb-0.5">
        {localizedMealType && (
          <>
            <span className="text-[12.4px] font-normal text-[#65635D] leading-[20px]">{localizedMealType}</span>
            <span className="text-[12.4px] font-normal text-[#C1C0BE] leading-[20px]">•</span>
          </>
        )}
        {cookingTimeMinutes && (
          <>
            <span className="text-[12.4px] font-normal text-[#65635D] leading-[20px]">
              {t('recipeCard.min', { time: cookingTimeMinutes })}
            </span>
            {freshDays > 0 && <span className="text-[12.4px] font-normal text-[#C1C0BE] leading-[20px]">•</span>}
          </>
        )}
        {freshDays > 0 && (
          <span className="text-[12.4px] font-normal text-[#65635D] leading-[20px]">
            {t('recipeCard.freshFor', { days: freshDays })}
          </span>
        )}
      </div>

      {/* Title */}
      <h1 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[20px] font-bold leading-[30px] tracking-[-0.01em] w-full mb-3">
        {recipe.title}
      </h1>

      {/* Description */}
      {description && (
        <p className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14px] leading-[24px] mb-5">
          {description}
        </p>
      )}

      {/* Start Cooking */}
      <button
        onClick={handleStartCooking}
        className="
          flex items-center justify-center
          w-full h-[52px] px-6
          rounded-[12px]
          text-[#FFF] text-center
          text-[15px] font-semibold leading-[24px]
          md:hover:bg-[#060]
          bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
          shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_1px_rgba(0,102,0,0.6)]
          transition-colors
        "
      >
        {isCookingStarted
          ? t('actions.continueCooking', 'Continue')
          : tRecipes('cookingMode.startCooking', 'Start Cooking')}
      </button>

      {/* Lower sections (ingredients, instructions, tips, equipment) */}
      <RecipeContent
        recipe={recipe}
        productList={productList}
        promoMeal={null}
        stickyFooterNav
        hideDescription
      />
      </div>
      </div>
    </div>
  );
};

export default RecipeDetailPage;
